// src/contexts/GeneralSettingsContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useUserContext } from './UserContext';

const GeneralSettingsContext = createContext(null);

export const DEFAULT_SETTINGS = {
  fontColor: '#ffffff',
  fontFamily: 'Arial',
  fontSize: '24px',
  animationEnabled: false,
  animationType: 'fade',
  transitionSpeed: 5,
  showScanMe: true,
  scanInterval: 5,
  showQrCorner: false,
  qrCornerPosition: 'bottom-right',
  qrCornerSize: 80,
  bannerEnabled: false,
  bannerPosition: 'hautcentre',
  bannerText: '',
  bannerTextColor: '#ffffff',
  bannerFontSize: '24px',
  watermarkEnabled: true,
  bgColor: '#000000',
  backgroundImage: ''
};

const API_BASE = process.env.REACT_APP_API_URL;

// Si tu mappes POST sur le même chemin que GET, laisse les deux identiques :
const ENDPOINT_GET  = `${API_BASE}/general-settings`;
const ENDPOINT_POST = `${API_BASE}/general-settings`;


const GeneralSettingsProvider = ({ children }) => {
  const { tokens, userInfo } = useUserContext();
  const [generalSettings, setGeneralSettings] = useState(DEFAULT_SETTINGS);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoadingSettings(true);
      setError(null);

      const token = tokens?.accessToken;
      const username = userInfo?.username;
      if (!token || !username) throw new Error('Missing token or username');

      const res = await axios.get(
        `${ENDPOINT_GET}?username=${encodeURIComponent(username)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const payload = res?.data?.body ? JSON.parse(res.data.body) : res?.data || {};
      const fromDb = payload?.generalsettings || {};

      setGeneralSettings({ ...DEFAULT_SETTINGS, ...fromDb });
    } catch (e) {
      console.error('[GeneralSettings] load error:', e);
      setError(e?.message || 'Failed to load settings');
      setGeneralSettings({ ...DEFAULT_SETTINGS });
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => {
    if (tokens?.accessToken && userInfo?.username) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens?.accessToken, userInfo?.username]);

  const saveGeneralSettings = async (next) => {
    try {
      const token = tokens?.accessToken;
      const username = userInfo?.username;
      if (!token || !username) throw new Error('Missing token or username');

      const res = await axios.post(
        ENDPOINT_POST,
        { username, generalsettings: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const payload = res?.data?.body ? JSON.parse(res.data.body) : res?.data || {};
      const saved = payload?.generalsettings || next;

      setGeneralSettings({ ...DEFAULT_SETTINGS, ...saved });
      return { success: true };
    } catch (e) {
      console.error('[GeneralSettings] save error:', e);
      return { success: false, error: e?.message || 'Save failed' };
    }
  };

  const value = useMemo(() => ({
    generalSettings,
    loadingSettings,
    error,
    DEFAULT_SETTINGS,
    saveGeneralSettings,
    reloadGeneralSettings: load,
  }), [generalSettings, loadingSettings, error]);

  return (
    <GeneralSettingsContext.Provider value={value}>
      {children}
    </GeneralSettingsContext.Provider>
  );
};

export const useGeneralSettings = () => {
  const ctx = useContext(GeneralSettingsContext);
  if (!ctx) throw new Error('useGeneralSettings must be used within GeneralSettingsProvider');
  return ctx;
};
export { GeneralSettingsProvider };
